import apiClient from "utils/api/apiClient";

const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
];

export const uploadFileToS3 = async (file: File, authToken?: string) => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    throw new Error(
      "Invalid file type. Only JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX are allowed.",
    );
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File size cannot exceed 25 MB.");
  }

  const headers = authToken
    ? { Authorization: `Bearer ${authToken}` }
    : undefined;

  try {
    // 1️⃣ INIT UPLOAD
    const initRes = await apiClient.post(
      "/uploads/multipart/init",
      { extension: `.${file.name.split(".").pop()}`, mimeType: file.type },
      headers,
    );

    if (!initRes.data) throw new Error("Init upload failed");
    const { UploadId, Key } = initRes.data?.data;

    // 2️⃣ SPLIT FILE INTO CHUNKS
    const parts: any[] = [];
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      // 3️⃣ GET PRESIGNED URL
      const urlRes = await apiClient.post(
        "/uploads/multipart/url",
        { uploadId: UploadId, key: Key, partNumber },
        headers,
      );
      if (!urlRes.data) throw new Error("Presigned URL failed");
      const signedUrl = urlRes.data.data.preSignedUrl;
      // 4️⃣ UPLOAD CHUNK TO S3
      const uploadRes = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: blob,
      });
      if (!uploadRes.ok)
        throw new Error(`S3 upload failed: ${uploadRes.status}`);
      const etag = uploadRes.headers.get("etag")?.replace(/"/g, "") ?? "";

      parts.push({
        PartNumber: partNumber,
        ETag: etag,
      });
    }

    // 5️⃣ COMPLETE UPLOAD
    const completeRes = await apiClient.post(
      "/uploads/multipart/complete",
      { uploadId: UploadId, key: Key, parts },
      headers,
    );

    return { ...(completeRes.data || {}), key: Key };
  } catch (error) {
    console.error("Upload failed:", error);
    throw error;
  }
};
